import moment from 'moment-timezone';

import Constants from './Constants';

export default class Utils {
  static generateRandomLetterOptionString = (rounds, letterOptionsPerRound, sort) => {
    let randomLetterOptionString = "";
    const frequencyTotal = Constants.FREQUENCIES.reduce((a, b) => a + b.frequency, 0);

    for (let i = 0; i < rounds; i++) {
      const roundLetterOptionList = [];
      for (let j = 0; j < letterOptionsPerRound; j++) {
        let randomLetter;
        do {
          let R = Math.floor(Math.random() * frequencyTotal);
          for (let k = 0; k < Constants.FREQUENCIES.length; k++) {
            R -= Constants.FREQUENCIES[k].frequency;
            if (R < 0) {
              randomLetter = Constants.FREQUENCIES[k].letter;
              break;
            }
          }
        } while (roundLetterOptionList.includes(randomLetter));

        roundLetterOptionList.push(randomLetter);
      }

      randomLetterOptionString += roundLetterOptionList.toString().replace(/,/g, "");
    }

    return randomLetterOptionString;
  }

  static generateRandomStartLetters = (rows, columns, startLetterCount) => {
    const randomStartLetters = {};
    for (let i = 0; i < startLetterCount; i++) {
      let coordinates;
      do {
        const row = Math.floor(Math.random() * rows);
        const column = Math.floor(Math.random() * columns);
        coordinates = "" + row + "," + column;
      } while (randomStartLetters[coordinates]);

      randomStartLetters[coordinates] = [String.fromCharCode(65 + Math.floor(Math.random() * 26))];
    }

    return randomStartLetters;
  }

  // Distinct random cells, avoiding any that are already spoken for. Used for
  // both the expanded cells and the bonus cells of a board, which is why it
  // names neither: the caller passes the cells already taken, so a second call
  // can be told to keep off the first call's.
  static generateRandomCoordinates = (rows, columns, count, disallowedCoordinates) => {
    const randomCoordinates = [];

    while (randomCoordinates.length < count) {
      const row = Math.floor(Math.random() * rows);
      const column = Math.floor(Math.random() * columns);
      const coordinates = "" + row + "," + column;

      if (!disallowedCoordinates.includes(coordinates) && !randomCoordinates.includes(coordinates)) {
        randomCoordinates.push(coordinates);
      }
    }

    return randomCoordinates;
  }

  static getDateStringToday = () => {
    return Utils.getDateStringDaysAgo(0);
  }

  static getDateStringYesterday = () => {
    return Utils.getDateStringDaysAgo(1);
  }

  // `Utils.`, not `this.`: a static arrow property keeps whatever `this` its
  // compiler gave it, and the two compilers here disagree. CRA's babel binds it
  // to the class, so this worked in every test and everywhere on the client;
  // babel-node with the es2015/stage-0 presets -- which is how the server runs
  // -- leaves it undefined, so the server threw here every time it looked for a
  // Monday. That is the whole of the weekly rollover.
  static getDateStringMonday = (date) => {
    while (Utils.getDayOfWeek(date) !== 1) {
      date = Utils.addDays(date, -1);
    }

    return date;
  }

  static getDateStringMostRecentMonday = () => {
    return Utils.getDateStringMonday(Utils.getDateStringToday());
  }

  static getDateStringTwoMondaysAgo = () => {
    return Utils.getDateStringMonday(Utils.getDateStringDaysAgo(7));
  }

  static getDateStringDaysAgo = (daysAgo) => {
    return moment(new Date()).tz("America/Toronto").add(-daysAgo, 'days').format("YYYY-MM-DD");
  }

  static getReadableDateStringToday = () => {
    return moment(new Date()).tz("America/Toronto").format("dddd, MMMM Do, YYYY");
  }

  static getDayOfWeekToday = () => {
    return moment(new Date()).tz("America/Toronto").day();
  }

  static getDayOfWeek = (date) => {
    return moment(date).day();
  }

  static getDaysAgo = (startDateString) => {
    var today = moment(new Date());
    var start = moment(startDateString);
    return moment.duration(today.diff(start)).asDays();
  }

  static addDays = (startDateString, days) => {
    return moment(startDateString).add(days, 'days').format("YYYY-MM-DD");
  }

  // Whole days from one date string to another, negative if the end precedes
  // the start. Deliberately parsed as UTC: a local-time diff across a daylight
  // saving boundary is 23 or 25 hours short of a whole number of days, which
  // truncates to a day less and would move a season boundary.
  static getDaysBetween = (startDateString, endDateString) => {
    return moment.utc(endDateString).diff(moment.utc(startDateString), 'days');
  }

  // Maps player name -> dense rank (0 for the best score, 1 for the next
  // distinct score, and so on). `scores` must be sorted descending; ties share
  // a rank, and any number of entries (including none) is fine. `maxRank`
  // stops the walk early when only the leaders are wanted.
  static getRankMap = (scores, maxRank = Infinity) => {
    const rankMap = {};
    let rank = -1;
    let previousScore = null;

    for (let i = 0; i < scores.length; i++) {
      if (scores[i].score !== previousScore) {
        rank++;
        previousScore = scores[i].score;
      }

      if (rank > maxRank) {
        break;
      }

      rankMap[scores[i].name] = rank;
    }

    return rankMap;
  }

  // The top three distinct scores, which is what a star, a medal or a trophy
  // is awarded for.
  static getAccoladeMap = (scores) => {
    return Utils.getRankMap(scores, 2);
  }

  static calculateTotalScore = (rowData, columnData) => {
    return rowData.reduce((a, b) => a + b.score, 0) + columnData.reduce((a, b) => a + b.score, 0);
  }

  // 1 -> "1st", 2 -> "2nd", 11 -> "11th". Used for season placings.
  static getOrdinal = (number) => {
    const lastTwo = number % 100;
    if (lastTwo >= 11 && lastTwo <= 13) {
      return `${number}th`;
    }

    const suffixes = ["th", "st", "nd", "rd"];
    return `${number}${suffixes[number % 10] || suffixes[0]}`;
  }

  static capitalize = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  // The flame's tooltip, in the header and on the profile alike. One copy, so
  // the two places the streak is shown cannot drift into describing it
  // differently. `atRisk` is a day whose daily is still unplayed, which is the
  // state the counter exists to warn about; a streak of nothing only reaches
  // here from the profile, since the header hides the counter at zero.
  //
  // A player has a *streak*, and never a "run" -- one word for this, wherever
  // it is written.
  static assembleStreakString = (streak, atRisk) => {
    if (!streak) {
      return "You don't have a streak right now.";
    }

    const dayNoun = streak === 1 ? "day" : "days";

    return `You have played the daily ${streak} ${dayNoun} in a row`
      + (atRisk ? ". Play today's to keep it going!" : ", today included.");
  }

  static assembleLockedPeriodSentence = (period, loggedIn) => {
    const ending = `to see ${Constants.SCORE_PERIOD_POSSESSIVES[period]} leaderboard.`;

    return loggedIn
      ? { action: "Play today's daily", remainder: ` ${ending}` }
      : { action: "Log in", remainder: ` and play today's daily ${ending}` };
  }

  // The increment day's explanation. The cycle runs 1 to 3 but does not
  // necessarily start at 1: `Seasons.getRoundLetterOptionCount` starts it at
  // the day's own `letterOptionsPerRound`, so that number is interpolated
  // rather than written into the sentence. Hardcoding it described every day but
  // the one increment day the schedule happens to hold.
  static assembleIncrementModeString = (startingAt) => {
    return `The number of letter options each turn follows a 1-2-3 pattern, starting with ${startingAt}.`;
  }

  static assembleScoreHistoryString = (scores, startDate, dayCount = 7) => {
    let scoreString = "";

    for (let days = 0; days < dayCount; days++) {
      const date = Utils.addDays(startDate, days);
      const score = scores[date];
      scoreString += score ? score : "0";
      if (days < dayCount - 1) {
        scoreString += " · ";
      }
    }

    return scoreString;
  }
}
